import { html, LitElement } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/core/lit-core.min.js';
import { cssProductOptions, cssProductOption, cssOptionItem } from './product-options.css.js?v=08092024';

const isItemAvailable = (item) => {
  if (
    item?.inventory_management === 'shopify' &&
    item?.inventory_policy !== 'continue' &&
    item?.quantity
  ) {
    return item.inventory_quantity >= item.quantity;
  }
  return item?.available !== false;
};

const isItemVisible = (item, selectedSku) => {
  if (!item?.showIfSelected || !item.showIfSelected.length || item.alwaysShowOn) {
    return true;
  }
  return selectedSku.some((sku) => item.showIfSelected.includes(sku));
};

class ProductOptions extends LitElement {
  static properties = {
    isAddingToCart: { type: Boolean },
    disableBtn: { type: Boolean },
    options: { type: Array },
    selectedItems: { type: Object },
    selectedSku: { type: Array },
    totalPrice: { type: Number },
    mainProduct: { type: Object },
    mainSKU: { type: String },
    isLoading: { type: Boolean},
    activeOptionIndex: { type: Array },
    aceDiscountEnabled: { type: Boolean },
    aceDiscountPercentage: { type: Number }
  };

  static get styles() {
    return [cssProductOptions];
  };

  constructor () {
    super();
    this.isAddingToCart = false;
    this.disableBtn = false;
    this.options = [];
    this.selectedItems = {};
    this.selectedSku = [];
    this.totalPrice = 0;
    this.isLoading = true;
    this.activeOptionIndex = [0];
    this.aceDiscountEnabled = false;
    this.aceDiscountPercentage = 15;
  }

  getSelectedSku() {
    return Object.values(this.selectedItems).map(item => item.sku).filter(item => item);
  }

  updateSelected () {
    this.options.forEach((option) => {
      if (option.items.length > 0 && !option.optional && !this.selectedItems[option.id] &&
        this.isOptionVisible(option)
      ) {
        const firstAvailableItem = option.items.find(
          item => isItemVisible(item, this.selectedSku) && isItemAvailable(item)
        )

        if (!firstAvailableItem) {
          return
        }

        this.selectedItems = { ...this.selectedItems, [option.id]: firstAvailableItem};
        this.selectedSku = [...this.selectedSku, firstAvailableItem.sku]
      }
    })
  }

  updated (changedProperties) {
    if (changedProperties.has('selectedItems')) {
      this.calculateTotalPrice();
      this.updatePriceElement();
    }

    if (changedProperties.has('selectedSku')) {
      this.updateSelected()
    }

    if (changedProperties.has('options') && this.options.length > 0) {
      this.updateSelected()

      if (this.isLoading) {
        this.isLoading = false
      }
    }
  }

  updatePriceElement() {
    const priceElement = document.querySelector('[data-main-product-price]');

    if (!priceElement) {
      return;
    }

    const displayPrice = this.getDisplayPrice();
    const originalPrice = this.getOriginalPrice();
    const isAceProduct = this.isAceProduct();

    if (isAceProduct && originalPrice) {
      priceElement.innerHTML = `
        <span class="product-price__reduced">${Shopify.formatMoney(displayPrice, "${{amount}}")}</span>
        <span class="product-price__compare">
          <span class="theme-money">${Shopify.formatMoney(originalPrice, "${{amount}}")}</span>
        </span>
        <span class="product-price__discount">(${this.aceDiscountPercentage}% OFF)</span>
      `;
    } else {
      priceElement.innerHTML = Shopify.formatMoney(displayPrice, "${{amount}}");
    }
  }

  handleSelection (optionId, item) {
    let currentIndexTab = this.options.findIndex((item) => item.id === optionId && item.items.length > 1);

    for (let currentIndex = currentIndexTab; currentIndex < this.options.length; currentIndex++) {
      const currentOption = this.options[currentIndex + 1]

      if (currentOption) {
        if (currentOption.items.length === 1 && (!currentOption.optional || isItemVisible(currentOption.items[0], this.selectedSku))) {
          currentIndexTab++
        } else {
          break
        }
      }
    }

    if (!this.activeOptionIndex.includes(currentIndexTab + 1)) {
      this.activeOptionIndex.push(currentIndexTab + 1);
    }

    this.selectedItems = { ...this.selectedItems, [optionId]: item };
    this.selectedSku = this.getSelectedSku();

    this.dropHiddenSelections();
    this.requestUpdate();
  }

  dropHiddenSelections () {
    let dropped = true;
    while (dropped) {
      dropped = false;
      const stillSelected = {};
      for (const [key, selected] of Object.entries(this.selectedItems)) {
        if (isItemVisible(selected, this.selectedSku)) {
          stillSelected[key] = selected;
        } else {
          dropped = true;
        }
      }
      this.selectedItems = stillSelected;
      this.selectedSku = this.getSelectedSku();
    }
  }

  isAceProduct () {
    if (!this.aceDiscountEnabled) {
      return false;
    }
    if (!this.mainProduct || !this.mainProduct.tags) {
      return false;
    }
    return this.mainProduct.tags.some(tag => tag.toLowerCase() === 'ace-product');
  }

  calculateTotalPrice () {
    this.totalPrice = Object.values(this.selectedItems).reduce((acc, item) => {
      if (item && item.price > 0) {
        acc += item.price * item.quantity
      }

      return acc
    }, 0);
  }

  getDisplayPrice () {
    if (this.isLoading) {
      return this.mainProduct?.compare_at_price || 0;
    }

    if (this.isAceProduct()) {
      const discountMultiplier = (100 - this.aceDiscountPercentage) / 100;
      return this.totalPrice * discountMultiplier;
    }

    return this.totalPrice;
  }

  getOriginalPrice () {
    if (this.isLoading || !this.isAceProduct()) {
      return null;
    }
    return this.totalPrice;
  }

  canAddToCart () {
    if (this.isLoading || !this.options.length) {
      return false
    }

    return this.options
      .filter(option => !option.optional && this.isOptionVisible(option))
      .every(option => !!this.selectedItems[option.id])
  }

  addToCart () {
    if (this.canAddToCart()) {
      this.isAddingToCart = true
      this.disableBtn = true
      const identityTimeStamp = new Date().getTime();

      // Create properties for includes product
      const selectedItemsFiltered = Object.fromEntries(Object.entries(this.selectedItems).filter(([key, value]) => !String(value.id).includes('sub-')));
      const isAceProduct = this.isAceProduct();
      let items = Object.values(selectedItemsFiltered).map((item,index) => {
        const optionKey = Object.keys(selectedItemsFiltered)[index]
        const optionName = this.options.find((item) => item.id === optionKey).name
        const properties = {
          'Package': this.mainProduct.title,
          'Package SKU': this.mainProduct.variants[0].sku,
          '_main_product_image': this.mainProduct.featured_image,
          '_main_product_title': this.mainProduct.title,
          '_main_product_url': `/products/${this.mainProduct.handle}`,
          '_identity': identityTimeStamp,
          '_quantity': item.quantity,
          '_field': optionKey,
          '_name': optionName
        };

        if (isAceProduct) {
          properties['_is_ace_bundle'] = true;
        }

        return {
          // check if id includes '-', take the ID before '-'
          'id': item.id.toString().includes('-') ? parseInt(item.id.toString().split('-')[0]) : item.id,
          'quantity': item.quantity,
          'properties': properties
        }
      }).sort((a, b) => this.options.find((i) => i.id === b.properties._field).order - this.options.find((i) => i.id === a.properties._field).order);

      let formData = {
        'items': items
      };

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
        .then(response => {
          if (response.status === 200) {
            $.get('/search', function(data){
              var selectors = [
                '.page-header .header-cart',
                '.docked-navigation-container .header-cart'
              ];
              var $parsed = $($.parseHTML('<div>' + data + '</div>'));
              for(var i=0; i<selectors.length; i++) {
                var cartSummarySelector = selectors[i];
                var $newCartObj = $parsed.find(cartSummarySelector).clone();
                var $currCart = $(cartSummarySelector);
                $currCart.replaceWith($newCartObj);
              }
              theme.checkCurrency();
            });
          }
        })
        .then (() => {
          this.isAddingToCart = false
          this.disableBtn = false
          window.location.href = `/cart`
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  }

  isOptionVisible(option) {
    if (option.hasAlwaysShowOn) {
      return true
    }

    return option.items.some(item => isItemVisible(item, this.selectedSku));
  }

  getUnavailableRequiredOptions () {
    return this.options.filter(option =>
      !option.optional && this.isOptionVisible(option) &&
      !option.items.some(item => isItemVisible(item, this.selectedSku) && isItemAvailable(item))
    )
  }

  render () {
    const displayPrice = this.getDisplayPrice();
    const originalPrice = this.getOriginalPrice();
    const isAceProduct = this.isAceProduct();
    const unavailableOptions = this.getUnavailableRequiredOptions();

    return html`
      <div class="advanced-option">
        <div class="custom-sku">SKU: <span>${this.mainSKU}</span></div>

        ${this.options.map((option, index) => this.isOptionVisible(option) ? html`
          <product-option
            .option=${option}
            .selectedSku=${this.selectedSku}
            .selectedItem=${this.selectedItems[option.id]}
            @selection-changed=${e => this.handleSelection(option.id, e.detail)}
          ></product-option>
        ` : '')}
        ${unavailableOptions.length ? html`
          <div class="option-unavailable-notice">
            ${unavailableOptions.map(option => option.name).join(', ')} ${unavailableOptions.length === 1 ? 'is' : 'are'} out of stock, so this bundle can't be added to cart.
          </div>
        ` : ''}
        <button @click=${this.addToCart} class="btn btn--primary v:button--primary" ?disabled=${!this.canAddToCart()}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16"><path fill="currentColor" d="M14.576 3.52A.75.75 0 0 0 14 3.25H4.125l-.342-1.884A.75.75 0 0 0 3.046.75H1.5a.75.75 0 0 0 0 1.5h.92l1.556 8.563c.042.227.129.444.255.638a2 2 0 1 0 3.457.799h2.125a2 2 0 1 0 1.937-1.5H5.698a.25.25 0 0 1-.246-.205l-.144-.795h6.948a1.75 1.75 0 0 0 1.722-1.437l.76-4.18a.75.75 0 0 0-.162-.614M6.25 12.75a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0m5.5.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1m.75-5.205a.25.25 0 0 1-.244.205H5.035l-.636-3.5H13.1z"></path></svg>
          ${this.isAddingToCart ? "Adding to cart" : "Add to cart"}
        </button>
      </div>
    `;
  }
}

class ProductOption extends LitElement {
  static properties = {
    option: { type: Object },
    selectedSku: { type: Array },
    selectedItems: { type: Array },
    isOpen: { type: Boolean },
    selectedItem: { type: Object },
  };

  static get styles() {
    return [cssProductOption];
  };

  constructor () {
    super();
    this.isOpen = true;
    this.selectedItem = null;
  }

  toggleOpen () {
    this.isOpen = !this.isOpen;
    this.handleSelection(this.selectedItem);
  }

  handleSelection (item) {
    if (!item) { return }
    this.selectedItem = { id: item.id };
    this.dispatchEvent(new CustomEvent('selection-changed', {
      detail: item
    }));
  }

  render () {
    const items = (
      this.option.optional &&
      !this.option.items.find(item => item.id === `sub-no-${this.option.name}`)
    ) ?
      [
        {
          id: `sub-no-${this.option.name}`,
          name: this.option.optionalLabel || `No ${this.option.name}`,
          price: 0,
          order: 0,
          quantity: 1,
          selected: true,
          showIfSelected: [],
          sku: `sub-no-${this.option.name}`,
          available: true
        },
        ...this.option.items
      ]
      : this.option.items;

    // Select the first item by default
    this.selectedItem = this.selectedItem || items.find(
      item => item.selected && isItemVisible(item, this.selectedSku)
    );
    // If there is only one item and it is optional, select it by default
    if (this.option.items.length === 1) {
      this.option.items = items
      // Use items instead of this.option.items => has no optional option
      this.selectedItem = this.option.items.find(
        item => isItemVisible(item, this.selectedSku) && isItemAvailable(item)
      ) || this.selectedItem;
    }

    const filteredItems = items.filter(item => isItemVisible(item, this.selectedSku));

    return html`
      <div class="product-option">
        <button type="button" class="product-option-toggle" @click=${() => this.toggleOpen()}>
          <div>${this.option.name}</div>
          <div class="product-option-right">
            <div class="product-option-arrow">${this.isOpen? html`<i class="arrow up"></i>`:html`<i class="arrow down"></i>`}</div>
          </div>
        </button>
        <div class=${this.isOpen ? 'product-option-items product-option-items-open' : 'product-option-items'}>
          <div class="product-option-items-inner">
            ${filteredItems.map((item, index) => html`
            <option-item
              .item=${item}
              .optionId=${this.option.id}
              .selected=${this.selectedItem?.id === item.id}
              @item-selected=${e => this.handleSelection(e.detail)}
            ></option-item>`
          )}
          </div>
        </div>
      </div>
    `;
  }
}

class OptionItem extends LitElement {
  static properties = {
    item: { type: Object },
    optionId: { type: String },
    selected: { type: Boolean }
  };

  static get styles() {
    return [cssOptionItem];
  };

  isDisabled() {
    return !isItemAvailable(this.item);
  }

  handleSelection () {
    this.dispatchEvent(new CustomEvent('item-selected', {
      detail: this.item
    }));
  }

  render () {
    return html`
      <div class="radio-input">
        <input
          class="radio-input-input"
          ?disabled=${this.isDisabled()}
          type="radio"
          name=${this.optionId}
          id=${this.optionId}
          value=${this.item.id}
          .checked=${this.selected}
          @change=${this.handleSelection}
        />
        <label class="radio-input-label" for=${this.optionId}>
          <span>${this.item.quantity > 1 ? html`${this.item.quantity} x ` : ''}${this.item.name}</span>${this.isDisabled() ? html`<span class="sold-out">Sold out</span>` : ''}${this.item.price > 0 ? html`<span class="price">+${Shopify.formatMoney(this.item.price * this.item.quantity, "${{amount}}")}</span>` : ''}
        </label>
      </div>
    `;
  }
}

customElements.define('product-options', ProductOptions);
customElements.define('product-option', ProductOption);
customElements.define('option-item', OptionItem);
